/**
 * Routes that render for someone with NO portal session (2026-08-27).
 *
 * ONE list, three consumers — keep it that way:
 *   - AuthContext: skips the session probe, so the bootstrap cannot
 *     window.location.replace() a public visitor to the portal login.
 *   - main.tsx: stamps data-public-page on <html> before React mounts, which
 *     the rule in index.html used to hide the shared NADC navbar (no bar and
 *     no rule since 2026-10-09; the flag stays — see main.tsx).
 *   - This file is the only place the paths appear.
 *
 * CHROMELESS_PATH_PATTERNS below is the same idea for pages that DO need a
 * session and still want the navbar gone. Same file, because this is still
 * the only place the paths appear.
 *
 * It lives in bundled JS rather than an inline <script> in index.html because
 * the CSP is `script-src 'self' https://nadc1.net` — an inline script is
 * silently dropped, which is exactly how the first attempt failed.
 */
export const PUBLIC_PATH_PATTERNS: RegExp[] = [
  /^\/my-schedule\//,
  /^\/sms-opt-in\/?$/,
  /^\/terms\/?$/,
  /^\/privacy\/?$/,
  // The public mileage page (company-trucks spec 2026-09-10 §5.3), served to
  // mileage.nadc1.net. Without this entry the App shell showed a spinner for
  // ever on that host: AuthContext probed the session, got no portal cookie,
  // and never cleared isLoading — caught on the first live visit.
  /^\/mileage\/?$/,
];

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PATTERNS.some((re) => re.test(pathname));
}

/**
 * Routes that DO need a portal session but carry no app chrome (2026-09-24).
 *
 * Deliberately separate from PUBLIC_PATH_PATTERNS. "Public" means skip the
 * session probe; putting the wallboard in that list would stop it
 * authenticating at all. What it actually needs is narrower: it is signed in,
 * and it wants the shared navbar gone.
 *
 * The navbar is a FIXED 80px bar injected by nadc1.net outside React, so it
 * sits on top of anything that fills the viewport — which on a television
 * left in the corner of the office is the top of the board.
 */
export const CHROMELESS_PATH_PATTERNS: RegExp[] = [
  /^\/equipment\/wallboard\/?$/,
];

export function isChromelessPath(pathname: string): boolean {
  return CHROMELESS_PATH_PATTERNS.some((re) => re.test(pathname));
}
